/**
 * Connected Experiences API Mappers
 *
 * Maps between frontend and backend for connected experience operations.
 *
 * Verified against:
 * - Backend: transformer_api/structs/connectedExperience.go
 * - Backend: transformer_api/connectedExperiences.go
 *
 * Backend expects camelCase for these fields (from JSON tags):
 * - id, tenantId, virtualTenantId, appId, name, recipeBaseIds, wizardGuids, type, reports
 * - lastModified, lastModifiedBy, createdDate, createdBy
 */
import type {
  Comment,
  ConnectedExperience,
  CreateCommentRequest,
  CreateConnectedExperienceRequest,
} from '@/types';

/**
 * Transform create connected experience request from frontend to backend format
 *
 * Backend expects:
 * {
 *   id?: number,
 *   name: string,
 *   recipeBaseIds: number[],
 *   wizardGuids: string[],
 *   type: string,
 *   reports: Array<{ url: string, environmentId: number }>
 * }
 *
 * Note: Backend uses camelCase (not PascalCase) as confirmed by JSON tags
 */
export const transformCreateConnectedExperienceRequest = (
  data: CreateConnectedExperienceRequest
): Record<string, unknown> => {
  return {
    id: data.id || 0,
    name: data.name,
    recipeBaseIds: data.recipeBaseIds || [],
    wizardGuids: data.wizardGuids || [],
    type: data.type || '',
    reports: data.reports || [],
  };
};

/**
 * Transform create comment request from frontend to backend format
 *
 * Backend expects:
 * {
 *   username: string,
 *   userId: number,
 *   comment: string
 * }
 */
export const transformCreateCommentRequest = (
  data: CreateCommentRequest
): Record<string, unknown> => {
  return {
    username: data.username,
    userId: data.userId,
    comment: data.comment,
  };
};

/**
 * Transform connected experience response from backend to frontend format
 * Backend sends camelCase, frontend expects camelCase, so minimal transformation needed
 */
export const transformConnectedExperienceResponse = (data: unknown): ConnectedExperience => {
  return data as ConnectedExperience;
};

/**
 * Transform comment response from backend to frontend format
 * Backend sends camelCase, frontend expects camelCase, so minimal transformation needed
 */
export const transformCommentResponse = (data: unknown): Comment => {
  return data as Comment;
};
